import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { TimesheetPageShiftFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import type Store from 'modules/attendance/components/ClockIn/Store'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import { IUseGetEmployeeProjectsTrackingMonthReturn } from 'modules/project_management/hooks/useGetEmployeeProjectsTrackingByMonth'

import AddLocationsModal from './AddLocationsModal'
import AddObservationsModal from './AddObservationsModal'
import AddProjectTimeRecordModal from './AddProjectTimeRecordModal'

interface Props {
  store: Store
  shifts: TimesheetPageShiftFragment[]
  readonly: boolean
  projectsContext: IUseGetEmployeeProjectsTrackingMonthReturn | null
  hasProjectsToTrackAndEnabled: boolean | undefined
  hasEditTimesheetAuthorization: boolean
  started: boolean
  canManageProjects: boolean
  noOptions: boolean
}

interface Option {
  onClick: () => void
  title: string
  description: string
  disabled?: boolean
}

const OptionsButton = ({
  store,
  shifts,
  readonly,
  projectsContext,
  hasProjectsToTrackAndEnabled,
  hasEditTimesheetAuthorization,
  started,
  canManageProjects,
  noOptions,
}: Props) => {
  const [modalOpen, setModalOpen] = React.useState<boolean>(false)
  const [modalContent, setModalContent] = React.useState<string>('')
  const hasWorkLocation = companies.current.attributes.has_clock_in_location_enabled

  const onClickOpenModal = (content: string) => {
    setModalOpen(true)
    setModalContent(content)
  }

  const onCloseModal = () => {
    setModalOpen(false)
  }

  const options = (): Option[] => {
    const optionsList = []

    if (!(readonly || !started)) {
      optionsList.push({
        onClick: () => onClickOpenModal('observations'),
        title: i18n.t('clock_in.observations.add_edit.title'),
        description: i18n.t('clock_in.observations.description'),
        disabled: hasEditTimesheetAuthorization,
      })
    }

    if (hasWorkLocation) {
      optionsList.push({
        onClick: () => onClickOpenModal('locations'),
        title: i18n.t('clock_in.locations.add_edit.title'),
        description: i18n.t('clock_in.locations.description'),
        disabled: hasEditTimesheetAuthorization,
      })
    }

    if (hasProjectsToTrackAndEnabled && canManageProjects) {
      optionsList.push({
        onClick: () => {
          onClickOpenModal('projectTimeRecord')
          track(trackingEvents.projectManagement.projectTimeRecords.recordProjectTimeStart, {
            device_name: 'Desktop',
          })
        },
        title: i18n.t('clock_in.projects.add_edit.title'),
        description: i18n.t('clock_in.projects.description'),
      })
    }

    return optionsList
  }

  const openModal = () => {
    const date = stringToDate(shifts[0]?.date || '', 'backend')
    const sortedShifts = shifts.sort(
      (a, b) => AttendanceShiftUtils.clockIn(a).unix() - AttendanceShiftUtils.clockIn(b).unix()
    )

    const modalProps = {
      onClose: onCloseModal,
      shifts: sortedShifts,
      store,
      date,
    }

    switch (modalContent) {
      case 'observations':
        return <AddObservationsModal {...modalProps} />
      case 'locations':
        return <AddLocationsModal {...modalProps} />
      case 'projectTimeRecord':
        if (!projectsContext?.queryData) return null
        return (
          <AddProjectTimeRecordModal
            projectsData={projectsContext?.queryData}
            shifts={shifts}
            date={date}
            onClose={onCloseModal}
          />
        )
      default:
        return null
    }
  }

  if (modalOpen) {
    return openModal()
  }

  if (noOptions) {
    return (
      <Button.Decorative
        type="secondary"
        iconName="ellipsis"
        disabled
        extra={{ 'data-intercom-target': 'attendance-row-options-button' }}
      />
    )
  }

  return (
    <PopoverMenu type="list">
      <PopoverMenu.Trigger>
        <Button.Decorative
          type="secondary"
          iconName="ellipsis"
          aria-label="popover trigger"
          extra={{ 'data-intercom-target': 'attendance-row-options-button' }}
        />
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="end">
        {options().map((option) => (
          <PopoverMenu.TouchableOption
            key={option.title}
            title={option.title}
            description={option.description}
            onClick={option.onClick}
            disabled={option.disabled}
          />
        ))}
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

export default observer(OptionsButton)
